/**
 * r2Storage.ts — serviço de alto nível para interagir com o Cloudflare R2
 * através da Edge Function `upload-r2`.
 *
 * Use este módulo (e o hook `useR2Upload`) no frontend.
 * As credenciais R2 NUNCA chegam ao browser — ficam nos Secrets da Edge Function.
 */

import { supabase } from "@/shared/integrations/supabase/client";
import type {
  R2DeleteOptions,
  R2DeletePayload,
  R2Modulo,
  R2PresignOptions,
  R2PresignPayload,
  R2PresignResponse,
  R2PresignUploadPayload,
  R2PresignUploadResponse,
  R2UploadOptions,
  R2UploadResult,
} from "@/shared/types/r2";

const EDGE_FUNCTION = "upload-r2";

/**
 * Em `localhost` / `127.0.0.1` + build de desenvolvimento, o PUT presigned para o R2
 * costuma falhar por CORS (origem não está na política do bucket). O fallback via
 * Edge Function já cobre isso — pular o presigned evita XHR inútil e `console.warn`
 * no Inspetor / relatório de erros DEV.
 */
function preferR2ProxyUploadNoDevLocal(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1";
}

/**
 * Monta URL pública do R2 codificando cada segmento do path (#, ?, espaços, etc.).
 * Sem isso, o navegador trata `#` como fragmento e o GET trunca o caminho (404).
 */
export function montarUrlPublicaR2(publicBaseUrl: string, key: string): string {
  const base = publicBaseUrl.replace(/\/$/, "");
  const normalized = key.replace(/^\/+/, "");
  const encoded = normalized
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  return encoded ? `${base}/${encoded}` : base;
}

/** Extrai mensagem de erro de uma resposta XML do R2 (sem dependências externas) */
function extrairMensagemXmlR2(xml: string): string | null {
  const match = /<Message>([^<]+)<\/Message>/i.exec(xml);
  return match ? match[1] : null;
}

/**
 * Faz PUT direto no R2 via XMLHttpRequest para expor progresso real.
 * Retorna a ETag do upload.
 */
function xhrPutR2(
  presignedUrl: string,
  file: File,
  contentType: string,
  onProgress?: (percent: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presignedUrl, true);
    xhr.setRequestHeader("Content-Type", contentType);

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.getResponseHeader("ETag") ?? "");
      } else {
        const mensagemXml = extrairMensagemXmlR2(xhr.responseText);

        let mensagemUsuario: string;
        if (xhr.status === 404) {
          mensagemUsuario = "Configuração de storage incorreta. Contate o administrador. (R2: Bucket not found)";
        } else if (xhr.status === 403) {
          mensagemUsuario = "Sem permissão para enviar arquivo. Contate o administrador. (R2: Access Denied)";
        } else if (xhr.status === 413) {
          mensagemUsuario = "Arquivo excede o tamanho máximo permitido.";
        } else if (xhr.status === 0) {
          mensagemUsuario = "Falha de rede ao enviar arquivo. Verifique sua conexão.";
        } else {
          mensagemUsuario = mensagemXml ?? `Erro ao enviar arquivo [${xhr.status}]`;
        }

        console.error(`[r2Storage] PUT falhou | status=${xhr.status} | response=${xhr.responseText?.substring(0, 300)}`);
        reject(new Error(mensagemUsuario));
      }
    };

    xhr.onerror = () => reject(new Error("Falha de rede ao enviar arquivo para o R2"));
    xhr.onabort = () => reject(new Error("Upload cancelado"));

    xhr.send(file);
  });
}

/**
 * Converte File para base64 data URL.
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Falha ao ler arquivo"));
    reader.readAsDataURL(file);
  });
}

const MAX_BASE64_FALLBACK_BYTES = 100 * 1024 * 1024; // 100 MB

/**
 * Upload via Edge Function com body base64 (fallback quando presigned PUT falha por CORS).
 * Funciona sem CORS porque o browser fala apenas com o Supabase, não com o R2 diretamente.
 */
async function uploadViaEdgeFunction(
  file: File,
  modulo: R2Modulo,
  contentType: string,
  prefixo?: string,
  onProgress?: (percent: number) => void,
): Promise<R2UploadResult> {
  if (file.size > MAX_BASE64_FALLBACK_BYTES) {
    throw new Error(
      `Arquivo muito grande para upload via proxy (${(file.size / 1024 / 1024).toFixed(0)} MB). ` +
      `Configure CORS no bucket R2 para permitir uploads presigned diretos.`
    );
  }

  onProgress?.(10);
  const fileBase64 = await fileToBase64(file);
  onProgress?.(40);

  const { data, error } = await supabase.functions.invoke<R2UploadResult & { error?: string }>(
    EDGE_FUNCTION,
    {
      body: {
        action: "upload",
        modulo,
        fileBase64,
        contentType,
        originalName: file.name,
        prefixo,
      },
    },
  );

  onProgress?.(90);

  if (error || data?.error) {
    throw new Error(data?.error ?? error?.message ?? "Erro no upload via Edge Function");
  }

  if (!data?.key) {
    throw new Error("Resposta invalida da Edge Function upload-r2 (upload)");
  }

  onProgress?.(100);
  return { key: data.key, url: data.url ?? "", size: file.size, contentType };
}

/**
 * Faz upload de um arquivo para o R2.
 *
 * Estratégia:
 * 1. Tenta presigned PUT (upload direto browser→R2, mais rápido, com progresso real).
 * 2. Se falhar por CORS (comum em localhost), faz fallback via Edge Function (base64).
 */
export async function uploadParaR2(options: R2UploadOptions): Promise<R2UploadResult> {
  const { file, modulo, prefixo, onProgress } = options;
  const contentType = options.contentType ?? (file.type || "application/octet-stream");

  if (preferR2ProxyUploadNoDevLocal()) {
    return uploadViaEdgeFunction(file, modulo, contentType, prefixo, onProgress);
  }

  // Passo 1 — solicitar presigned PUT URL à Edge Function
  const presignPayload: R2PresignUploadPayload = {
    action: "presign-upload",
    modulo,
    originalName: file.name,
    contentType,
    size: file.size,
    prefixo,
  };

  const { data: presignData, error: presignError } =
    await supabase.functions.invoke<R2PresignUploadResponse>(EDGE_FUNCTION, {
      body: presignPayload,
    });

  if (presignError || presignData?.error) {
    const rawMsg = presignData?.error ?? presignError?.message ?? "Erro ao obter URL de upload";
    console.error("[r2Storage] Erro ao obter presigned URL:", rawMsg);

    let msg = rawMsg;
    if (rawMsg.includes("Bucket not found") || rawMsg.toLowerCase().includes("bucket")) {
      msg = "Configuração de storage incorreta no servidor. Contate o administrador.";
    } else if (rawMsg.includes("Configuração R2 incompleta")) {
      msg = "Storage de arquivos não configurado. Contate o administrador.";
    }

    throw new Error(msg);
  }

  if (!presignData?.presignedUrl || !presignData?.key) {
    throw new Error("Resposta inválida da Edge Function upload-r2 (presign-upload)");
  }

  const { presignedUrl, key } = presignData;

  // Passo 2 — PUT direto no R2 com progresso real via XHR
  onProgress?.(0);
  try {
    await xhrPutR2(presignedUrl, file, contentType, onProgress);
    onProgress?.(100);
    try {
      await supabase.functions.invoke(EDGE_FUNCTION, {
        body: { action: "audit-upload", key, fileSize: file.size },
      });
    } catch {
      /* auditoria não bloqueia o fluxo */
    }
  } catch (err) {
    const isCorsOrNetwork =
      err instanceof Error &&
      (err.message.includes("Falha de rede") || err.message.includes("network"));

    if (isCorsOrNetwork) {
      console.warn(
        "[r2Storage] Presigned PUT falhou (provavel CORS). Usando fallback via Edge Function...",
      );
      return uploadViaEdgeFunction(file, modulo, contentType, prefixo, onProgress);
    }

    throw err;
  }

  // Passo 3 — construir URL pública
  const publicBase = import.meta.env.VITE_R2_PUBLIC_URL as string | undefined;
  const url = publicBase ? montarUrlPublicaR2(publicBase, key) : "";

  return { key, url, size: file.size, contentType };
}

/**
 * Sobrescreve um objeto existente no R2 (PUT na chave exata).
 * Usa `presign-upload-raw` na Edge Function — não gera novo timestamp no path.
 */
export async function uploadBlobPresignChaveExata(options: {
  key: string;
  blob: Blob;
  contentType: string;
  onProgress?: (percent: number) => void;
}): Promise<void> {
  const { key, blob, contentType, onProgress } = options;
  const nomeArquivo = key.split("/").pop() ?? "arquivo";
  const file = new File([blob], nomeArquivo, { type: contentType });

  const { data, error } = await supabase.functions.invoke<{
    presignedUrl?: string;
    error?: string;
  }>(EDGE_FUNCTION, {
    body: {
      action: "presign-upload-raw",
      key,
      contentType,
      size: blob.size,
    },
  });

  if (error || data?.error || !data?.presignedUrl) {
    throw new Error(data?.error ?? error?.message ?? "Falha ao obter URL para salvar no armazenamento");
  }

  onProgress?.(0);
  await xhrPutR2(data.presignedUrl, file, contentType, onProgress);
  onProgress?.(100);
  try {
    await supabase.functions.invoke(EDGE_FUNCTION, {
      body: { action: "audit-upload", key, fileSize: blob.size },
    });
  } catch {
    /* auditoria não bloqueia */
  }
}

/**
 * Remove um arquivo do R2 via Edge Function.
 */
export async function deletarDoR2(options: R2DeleteOptions): Promise<void> {
  const payload: R2DeletePayload = { action: "delete", key: options.key };

  const { data, error } = await supabase.functions.invoke<{ ok: boolean; error?: string }>(
    EDGE_FUNCTION,
    { body: payload },
  );

  if (error || data?.error) {
    const msg = data?.error ?? error?.message ?? "Erro ao remover arquivo do R2";
    throw new Error(msg);
  }
}

/**
 * Abre um ficheiro R2 numa nova aba.
 * Com `VITE_R2_PUBLIC_URL`, usa URL pública codificada (evita Edge e problemas com `#` no path).
 * Caso contrário, obtém URL via Edge (`presign`).
 */
export async function abrirChaveR2EmNovaAba(key: string): Promise<void> {
  const k = key.trim();
  if (!k) {
    throw new Error("Chave do arquivo indisponível.");
  }
  if (k.startsWith("http://") || k.startsWith("https://")) {
    window.open(k, "_blank", "noopener,noreferrer");
    return;
  }
  const publicBase = import.meta.env.VITE_R2_PUBLIC_URL as string | undefined;
  if (publicBase) {
    window.open(montarUrlPublicaR2(publicBase, k), "_blank", "noopener,noreferrer");
    return;
  }
  const url = await obterUrlR2({ key: k });
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Obtém URL pública (ou assinada) de um arquivo no R2.
 */
export async function obterUrlR2(options: R2PresignOptions): Promise<string> {
  const payload: R2PresignPayload = {
    action: "presign",
    key: options.key,
    expiresIn: options.expiresIn,
  };

  const { data, error } = await supabase.functions.invoke<R2PresignResponse>(EDGE_FUNCTION, {
    body: payload,
  });

  if (error || (data as R2PresignResponse & { error?: string })?.error) {
    const msg = (data as R2PresignResponse & { error?: string })?.error ?? error?.message ?? "Erro ao obter URL do R2";
    throw new Error(msg);
  }

  return data!.url;
}

/**
 * Determina se um arquivo deve ir para o R2 (pesado) ou Supabase Storage (leve).
 * Regra: arquivos > 5 MB vão para R2; demais ficam no Supabase Storage.
 */
export function deveUsarR2(file: File): boolean {
  const SUPABASE_STORAGE_LIMIT = 5 * 1024 * 1024; // 5 MB
  return file.size > SUPABASE_STORAGE_LIMIT;
}

/**
 * Resolve o storage correto baseado no tamanho do arquivo.
 * Retorna "r2" ou "supabase".
 */
export function resolverStorage(file: File): "r2" | "supabase" {
  return deveUsarR2(file) ? "r2" : "supabase";
}

/**
 * Registra um evento de auditoria no R2 (preview, word-open, word-save, view, etc.).
 * Chamada assíncrona e não bloqueante — falhas são ignoradas.
 */
export async function registrarEventoR2(params: {
  event: string;
  key: string;
  keyDestino?: string;
  fileSize?: number;
}): Promise<void> {
  try {
    await supabase.functions.invoke(EDGE_FUNCTION, {
      body: {
        action: "audit-event",
        event: params.event,
        key: params.key,
        keyDestino: params.keyDestino,
        fileSize: params.fileSize,
      },
    });
  } catch {
    /* auditoria não bloqueia o fluxo */
  }
}

export type { R2Modulo, R2UploadOptions, R2UploadResult };

